import { EXPLORE_ROUTES } from "../homeDashboard/exploreRoutes";
import { REFERRAL_ROUTES } from "../homeDashboard/referralEarn/referralRoutes";
import { GAMIFICATION_ROUTES } from "../homeDashboard/gamification/gamificationRoutes";
import { EXAM_COURSE_ROUTES } from "../homeDashboard/courses/examCourseRoutes";
import { QUIZ_ROUTES } from "../homeDashboard/quizzes/quizRoutes";
import { CONTENT_ROUTES } from "../homeDashboard/contentRoutes";
import { AI_TUTOR_ROUTES } from "../homeDashboard/voiceChat/aiTutorRoutes";
const QUICK_ACTION_ROUTE_MAP = {
  mcqbank: EXPLORE_ROUTES.subjects,
  exams: EXPLORE_ROUTES.exams,
  "quiz&pyp": EXPLORE_ROUTES.exams,
  rapidfire: EXPLORE_ROUTES.exams,
  gamification: GAMIFICATION_ROUTES.start,
  "ai-tutor": AI_TUTOR_ROUTES.voiceChat,
  aitutor: AI_TUTOR_ROUTES.voiceChat,
  "voice-chat": AI_TUTOR_ROUTES.voiceChat,
  doubts: "/doubts",
  blogs: CONTENT_ROUTES.articles,
  "referral-earn": REFERRAL_ROUTES.home,
};

const FALLBACK_CARD_STYLES = [
  {
    cardBackground: "#eef4fe",
    cardBorder: "#dceaff",
    iconBackground: "linear-gradient(145deg, #edf5ff 0%, #dceaff 100%)",
    iconColor: "#1757e6",
  },
  {
    cardBackground: "#f4f2fd",
    cardBorder: "#e9deff",
    iconBackground: "linear-gradient(145deg, #f1ebff 0%, #e2d7ff 100%)",
    iconColor: "#6642f0",
  },
  {
    cardBackground: "#fdf5ed",
    cardBorder: "#ffe2bb",
    iconBackground: "linear-gradient(145deg, #fff3df 0%, #ffe2bb 100%)",
    iconColor: "#d88400",
  },
  {
    cardBackground: "#fff0ec",
    cardBorder: "#ffd3cc",
    iconBackground: "linear-gradient(145deg, #ffe8e8 0%, #ffd3cc 100%)",
    iconColor: "#ef6b4f",
  },
  {
    cardBackground: "#eef7ff",
    cardBorder: "#d9ecff",
    iconBackground: "linear-gradient(145deg, #eef7ff 0%, #d9ecff 100%)",
    iconColor: "#2d84df",
  },
];

const DASHBOARD_QUIZ_CATEGORY_ID = 1;
const QUESTION_BANK_CATEGORY_ID = 2;
const LECTURES_CATEGORY_ID = 3;
const RAPID_FIRE_CATEGORY_ID = 5;
const GAMIFICATION_CATEGORY_ID = 6;
const COURSES_CATEGORY_ID = 7;
const ARTICLES_CATEGORY_ID = 8;
const FLASH_CARDS_CATEGORY_ID = 9;
const PODCAST_CATEGORY_ID = 12;
const INFOGRAPHICS_CATEGORY_ID = 13;
const DAILY_QUESTIONS_CATEGORY_ID = 14;
const AI_TUTOR_CATEGORY_ID = 15;
const REFERRAL_CATEGORY_ID = 11;

function withHash(value, fallback) {
  if (!value || typeof value !== "string") {
    return fallback;
  }

  return value.startsWith("#") ? value : `#${value}`;
}

function resolveDashboardTitle(item) {
  return item?.title || item?.category_name || "Untitled";
}

function resolveDashboardProgress(item) {
  return item?.progress || `${item?.attempted_count || 0}/${item?.count || 0} Items`;
}

function resolveDashboardRoute(item) {
  const slug = `${item?.slug || ""}`.trim().toLowerCase();
  const title = resolveDashboardTitle(item).trim().toLowerCase();
  const categoryId = Number(item?.category_id || item?.id || 0);

  if (categoryId === REFERRAL_CATEGORY_ID) {
    return REFERRAL_ROUTES.home;
  }

  if (categoryId === AI_TUTOR_CATEGORY_ID || slug === "ai-tutor" || slug === "aitutor" || slug === "voice-chat" || title.includes("ai tutor")) {
    return AI_TUTOR_ROUTES.voiceChat;
  }

  if (slug === "referral-earn" || slug === "refer-and-earn" || title.includes("referral")) {
    return REFERRAL_ROUTES.home;
  }

  if (
    item?.route_key === 1 ||
    slug === "mcqbank" ||
    title.includes("question bank") ||
    Number(item?.category_id || item?.id || 0) === LECTURES_CATEGORY_ID ||
    categoryId === PODCAST_CATEGORY_ID
  ) {
    return EXPLORE_ROUTES.subjects;
  }

  if (
    slug === "gamification" ||
    title.includes("gamification") ||
    Number(item?.category_id || item?.id || 0) === GAMIFICATION_CATEGORY_ID
  ) {
    return GAMIFICATION_ROUTES.start;
  }

  if (Number(item?.category_id || item?.id || 0) === COURSES_CATEGORY_ID || slug === "courses") {
    return EXAM_COURSE_ROUTES.masterExams;
  }

  if (Number(item?.category_id || item?.id || 0) === ARTICLES_CATEGORY_ID || slug === "blogs") {
    return CONTENT_ROUTES.articles;
  }

  if (Number(item?.category_id || item?.id || 0) === INFOGRAPHICS_CATEGORY_ID || slug === "infographics") {
    return EXPLORE_ROUTES.subjects;
  }

  if (Number(item?.category_id || item?.id || 0) === DAILY_QUESTIONS_CATEGORY_ID || slug === "daily-questions" || title.includes("daily question")) {
    return CONTENT_ROUTES.dailyQuestions;
  }

  if (slug && QUICK_ACTION_ROUTE_MAP[slug]) {
    return QUICK_ACTION_ROUTE_MAP[slug];
  }

  if (title.includes("news") || title.includes("article")) {
    return CONTENT_ROUTES.articles;
  }

  return EXPLORE_ROUTES.exams;
}

export function buildQuickActionItems(stats) {
  return (Array.isArray(stats) ? stats : []).map((item, index) => {
    const fallback = FALLBACK_CARD_STYLES[index % FALLBACK_CARD_STYLES.length];

    return {
      id: `${item?.category_id || "category"}-${item?.route_key || item?.slug || index}-${index}`,
      title: resolveDashboardTitle(item),
      description: resolveDashboardProgress(item),
      image: item?.category_image || "",
      route: resolveDashboardRoute(item),
      styles: {
        backgroundColor: withHash(item?.background_color_code, fallback.cardBackground),
        borderColor: withHash(item?.border_color_code, fallback.cardBorder),
        iconBackground: fallback.iconBackground,
        iconColor: fallback.iconColor,
      },
    };
  });
}

export function buildHeaderQuickActionItems(categories) {
  return (Array.isArray(categories) ? categories : []).map((item, index) => {
    const fallback = FALLBACK_CARD_STYLES[index % FALLBACK_CARD_STYLES.length];
    const routeKey = `${item?.route_key ?? ""}`.trim().toLowerCase();
    const normalizedTitle = `${item?.title || ""}`.trim().toLowerCase();
    const slug = `${item?.slug || ""}`.trim().toLowerCase();
    const categoryId = Number(item?.category_id || item?.id || 0);

    let route = EXPLORE_ROUTES.exams;

    if (categoryId === REFERRAL_CATEGORY_ID) {
      route = REFERRAL_ROUTES.home;
    } else if (categoryId === AI_TUTOR_CATEGORY_ID || slug === "ai-tutor" || slug === "aitutor" || slug === "voice-chat" || normalizedTitle.includes("ai tutor")) {
      route = AI_TUTOR_ROUTES.voiceChat;
    } else if (categoryId === DASHBOARD_QUIZ_CATEGORY_ID) {
      route = QUIZ_ROUTES.list;
    } else if (
      categoryId === QUESTION_BANK_CATEGORY_ID ||
      categoryId === RAPID_FIRE_CATEGORY_ID ||
      categoryId === FLASH_CARDS_CATEGORY_ID ||
      categoryId === PODCAST_CATEGORY_ID ||
      categoryId === INFOGRAPHICS_CATEGORY_ID ||
      categoryId === LECTURES_CATEGORY_ID
    ) {
      route = EXPLORE_ROUTES.subjects;
    } else if (categoryId === GAMIFICATION_CATEGORY_ID) {
      route = GAMIFICATION_ROUTES.start;
    } else if (categoryId === DAILY_QUESTIONS_CATEGORY_ID) {
      route = CONTENT_ROUTES.dailyQuestions;
    } else if (categoryId === COURSES_CATEGORY_ID || `${item?.slug || ""}`.trim().toLowerCase() === "courses") {
      route = EXAM_COURSE_ROUTES.masterExams;
    } else if (categoryId === ARTICLES_CATEGORY_ID || routeKey === "8" || `${item?.slug || ""}`.trim().toLowerCase() === "blogs" || normalizedTitle.includes("news") || normalizedTitle.includes("article")) {
      route = CONTENT_ROUTES.articles;
    } else if (routeKey === "11" || slug === "referral-earn" || slug === "refer-and-earn" || normalizedTitle.includes("referral")) {
      route = REFERRAL_ROUTES.home;
    } else if (categoryId === 10 || `${item?.slug || ""}`.trim().toLowerCase() === "doubts" || normalizedTitle.includes("doubt")) {
      route = "/doubts";
    }

    return {
      id: `${item?.category_id || "header-category"}-${item?.route_key || index}-${index}`,
      title: item?.title || "Untitled",
      description: item?.description || "",
      image: item?.category_image || "",
      route,
      source: item,
      styles: {
        backgroundColor: withHash(item?.background_color_code, fallback.cardBackground),
        borderColor: withHash(item?.border_color_code, fallback.cardBorder),
        iconBackground: fallback.iconBackground,
        iconColor: fallback.iconColor,
      },
    };
  });
}

export function buildProgressItems(stats) {
  return (Array.isArray(stats) ? stats : []).map((item, index) => {
    const fallback = FALLBACK_CARD_STYLES[index % FALLBACK_CARD_STYLES.length];
    const progressCounts = `${item?.progress || ""}`.match(/([\d,]+)\s*\/\s*([\d,]+)/);
    const readCount = (value) => {
      if (value === undefined || value === null || value === "") return null;
      const count = Number(`${value}`.replace(/,/g, ""));
      return Number.isFinite(count) && count >= 0 ? count : null;
    };
    const completed = readCount(item?.attempted_count) ?? readCount(progressCounts?.[1]) ?? 0;
    const total = readCount(item?.count) ?? readCount(progressCounts?.[2]) ?? 0;
    const percentage = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;

    return {
      id: `${item?.category_id || "progress"}-${item?.route_key || item?.slug || index}-${index}`,
      value: resolveDashboardProgress(item),
      label: resolveDashboardTitle(item),
      image: item?.category_image || "",
      completed,
      total,
      percentage,
      toneIndex: index,
      styles: {
        backgroundColor: withHash(item?.background_color_code, fallback.cardBackground),
        borderColor: withHash(item?.border_color_code, fallback.cardBorder),
        iconBackground: fallback.iconBackground,
        iconColor: fallback.iconColor,
      },
    };
  });
}

export function buildRapidFireBattleItems(battles) {
  return (Array.isArray(battles) ? battles : []).map((item, index) => {
    const routeKey = `${item?.route_key ?? ""}`.trim().toLowerCase();
    const battleId = routeKey === "battle_1" || routeKey === "battle_2"
      ? Number(item?.battles_id || item?.battle_id || routeKey.slice(-1))
      : 0;
    const buttonLabel = routeKey === "battle_2" ? "Challenge Now" : "Play Now";
    const route = routeKey === "rapid_fire" || battleId > 0
      ? EXPLORE_ROUTES.subjects
      : GAMIFICATION_ROUTES.start;

    return {
      id: item?.id || `battle-${index}`,
      title: item?.name || "Game",
      description: item?.description || "",
      image: item?.image || "",
      buttonLabel,
      route,
      battleId,
    };
  });
}














